import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AppLayout } from '@scramble/layouts';
import { setUserToCookie } from '@/helpers/sharedUser';
import { changeUserLocale } from '@/helpers/locale';
import { APP_LINKS } from '@/helpers/links';
import { TopHeader } from '@/components/TopHeader';
import { AppFooter } from '@/components/AppFooter';
import { useGetMe } from '@/hooks/useGetMe';
import { ChatwootChat } from '@/chat/chatwoot';
import { useGetChatwootIdentity } from '@/query/queries/chatwoot/useGetChatwootIdentity';
import { padNumberUserId } from '@scramble/utils';
import { NAV_ROUTES } from './menu';

export const AppContainer = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { accessToken, user, profile, fullName } = useGetMe();

  const onLocaleChange = locale => changeUserLocale(i18n, locale);

  const { data: chatwootIdentity } = useGetChatwootIdentity({
    inbox: 'investor',
    enabled: !!accessToken && import.meta.env.VITE_CHAT_CHATWOOT === 'on',
  });

  const MENU = NAV_ROUTES(t);

  useEffect(() => {
    setUserToCookie(accessToken);
  }, [accessToken]);

  useEffect(() => {
    if (!chatwootIdentity || !profile) return;

    ChatwootChat.setUser({
      identifier: chatwootIdentity.identifier,
      identifierHash: chatwootIdentity.identifier_hash,
      email: profile.email,
      name: fullName,
      phoneNumber: profile.phone,
      avatarUrl: profile.photo?.file,
      countryCode: profile.country_code,
      companyName: profile.investor?.company_name,
    });
    ChatwootChat.setCustomAttributes({
      scramble_user_id: profile.id,
      role: profile.role,
      status: profile.status,
      country: profile.country,
      citizenship: profile.citizenship,
      citizenship_code: profile.citizenship_code,
      position: profile.investor?.position,
    });
  }, [chatwootIdentity, profile, fullName]);

  if (!accessToken) {
    return <Navigate to={APP_LINKS.auth} replace />;
  }

  if (location.pathname === APP_LINKS.root) {
    return <Navigate to={APP_LINKS.dashboard} replace />;
  }

  const userInfo = {
    id: padNumberUserId(user?.id),
    name: fullName,
    avatar: user?.photo?.file,
    link: APP_LINKS.profile,
  };

  return (
    <AppLayout
      menu={MENU}
      user={userInfo}
      footer={<AppFooter />}
      currentLocale={i18n.language}
      onLocaleChange={onLocaleChange}
    >
      <TopHeader />
      <Outlet />
    </AppLayout>
  );
};